import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { MdHowToVote, MdEdit, MdInfo, MdAccessTime, MdDateRange } from 'react-icons/md';
import ReportButton from './ReportButton';
import ShareButton from './ShareButton';
import DemographicBreakdownChart from './DemographicBreakdownChart';

function CustomPollCard({ poll: initialPoll, tags, isOwnerView = false, onPollUpdate }) {
  const [poll, setPoll] = useState(initialPoll);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [demographicBreakdown, setDemographicBreakdown] = useState(null);
  const [voting, setVoting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [demographics, setDemographics] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('overall');
  const [filterError, setFilterError] = useState('');
  const [isSwipingAway, setIsSwipingAway] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const pollTags = tags.filter(tag => poll.tags?.includes(tag.tagId));

  useEffect(() => {
    setPoll(initialPoll);
  }, [initialPoll]);

  // Auto-load results if owner or already answered
  useEffect(() => {
    if ((isOwnerView || poll.userAnswered) && poll.status !== 'pending' && poll.status !== 'rejected') {
      loadResults();
      loadDemographics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnerView, poll.userAnswered, poll.status]);

  const handleVote = async (answer) => {
    const isEditingExisting = poll.userAnswered && editing;
    const previousAnswer = poll.userAnswer;
    const previousResults = results;
    
    // Optimistic UI update - immediately show the new answer for edits
    if (isEditingExisting) {
      // Update local state immediately for responsive UI
      setPoll(prev => ({
        ...prev,
        userAnswer: answer
      }));
      
      // Optimistically update results if we have them
      if (results) {
        const oldAnswer = previousAnswer?.toLowerCase();
        const newAnswer = answer.toLowerCase();
        
        if (oldAnswer !== newAnswer) {
          setResults(prev => {
            if (!prev) return prev;
            const newYes = prev.yes + (newAnswer === 'yes' ? 1 : 0) - (oldAnswer === 'yes' ? 1 : 0);
            const newNo = prev.no + (newAnswer === 'no' ? 1 : 0) - (oldAnswer === 'no' ? 1 : 0);
            const total = newYes + newNo;
            return {
              ...prev,
              yes: newYes,
              no: newNo,
              yesPercentage: total > 0 ? Math.round((newYes / total) * 100) : 0,
              noPercentage: total > 0 ? Math.round((newNo / total) * 100) : 0
            };
          });
        }
      }
      
      setEditing(false);
      setVoting(false);
    } else {
      setVoting(true);
    }
    
    try {
      if (isEditingExisting) {
        // Fire and forget for edits - UI already updated
        axios.put(`${API_URL}/api/custom-polls/${poll.customPollId}/respond`, { answer })
          .catch(error => {
            // Revert on error
            console.error('Failed to update vote:', error);
            setPoll(prev => ({ ...prev, userAnswer: previousAnswer }));
            setResults(previousResults);
            alert(error.response?.data?.detail || 'Failed to update vote. Please try again.');
          });
        
        // Notify parent (don't remove from list for edits)
        if (onPollUpdate) {
          onPollUpdate(poll.customPollId, true);
        }
      } else {
        // For new votes, wait for response
        await axios.post(`${API_URL}/api/custom-polls/${poll.customPollId}/respond`, { answer });
        setVoting(false);
        
        // If NOT owner view, animate and hide the card
        if (!isOwnerView) {
          setIsSwipingAway(true);
          
          setTimeout(() => {
            setIsHidden(true);
            if (onPollUpdate) {
              onPollUpdate(poll.customPollId);
            }
          }, 500);
        } else {
          // Owner view - show results inline
          setPoll(prev => ({
            ...prev,
            userAnswered: true,
            userAnswer: answer
          }));
          loadResults();
          loadDemographics();
        }
      }
      
    } catch (error) {
      // Only reaches here for new votes (edits handle errors in .catch above)
      alert(error.response?.data?.detail || 'Failed to submit vote');
      setVoting(false);
    }
  };

  const loadDemographics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-polls/${poll.customPollId}/demographics`);
      setDemographics(response.data.demographics);
    } catch (error) {
      console.error('Failed to load demographics:', error);
    }
  };

  const loadResults = async () => {
    try {
      setFilterError('');
      
      if (!results) {
        setLoadingResults(true);
        const cumulativeUrl = `${API_URL}/api/custom-polls/${poll.customPollId}/results`;
        const cumulativeResponse = await axios.get(cumulativeUrl);
        setResults(cumulativeResponse.data);
        setLoadingResults(false);
      }
      
      setShowResults(true);
    } catch (error) {
      setLoadingResults(false);
      if (error.response?.status === 400) {
        setFilterError(error.response.data.detail);
      } else if (error.response?.status !== 403) {
        console.error('Failed to load results:', error);
      }
    }
  };

  const loadDemographicBreakdown = async (demographic) => {
    try {
      setFilterError('');
      setLoadingBreakdown(true);
      
      const response = await axios.get(`${API_URL}/api/custom-polls/${poll.customPollId}/demographic-breakdown?demographic=${demographic}`);
      setDemographicBreakdown(response.data);
      setLoadingBreakdown(false);
    } catch (error) {
      setLoadingBreakdown(false);
      if (error.response?.status === 400) {
        setFilterError(error.response.data.detail);
      } else {
        console.error('Failed to load demographic breakdown:', error);
      }
    }
  };

  const handleFilterChange = async (filterType) => {
    setSelectedFilter(filterType);
    setFilterError('');
    
    if (filterType === 'overall') {
      setDemographicBreakdown(null);
    } else {
      await loadDemographicBreakdown(filterType);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Processing' },
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      flagged: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Under Review' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium font-rubik ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPollTypeLabel = (durationDays) => {
    switch (durationDays) {
      case 1:
        return { label: '1 Day Poll', color: 'bg-blue-500' };
      case 7:
        return { label: '7 Day Poll', color: 'bg-purple-500' };
      case 30:
        return { label: '30 Day Poll', color: 'bg-orange-500' };
      default:
        return { label: `${durationDays} Day Poll`, color: 'bg-gray-500' };
    }
  };

  const cumulativeBarChartData = results ? [
    { name: 'Yes', value: results.results.yes.count, color: '#08B23C', percentage: results.results.yes.percentage },
    { name: 'No', value: results.results.no.count, color: '#D33D3D', percentage: results.results.no.percentage }
  ] : [];

  // Hide the card completely after swipe animation
  if (isHidden) {
    return null;
  }

  return (
    <div 
      className={`poll-card ${isSwipingAway ? 'swipe-away' : ''}`} 
      data-testid={`custom-poll-card-${poll.customPollId}`}
    >
      {/* Header - Only show status badge for owner view */}
      {isOwnerView && (
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-3">
            {getStatusBadge(poll.status)}
          </div>
        </div>
      )}

      {/* Question */}
      <div className="mb-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-semibold text-gray-800 flex-1 font-rubik" data-testid="custom-poll-question">
            {poll.question}
          </h3>
          <div className="flex items-center gap-3 ml-4">
            {/* Share button - show for active polls OR for owner view on active/closed polls */}
            {(poll.status === 'active' || (isOwnerView && (poll.status === 'active' || poll.status === 'closed'))) && (
              <ShareButton 
                pollId={poll.customPollId} 
                pollQuestion={poll.question}
                isCustomPoll={true}
              />
            )}
            {/* Report button - only show on All Polls page (not owner view) for active polls */}
            {poll.status === 'active' && !isOwnerView && (
              <ReportButton 
                pollId={poll.customPollId} 
                isCustomPoll={true}
              />
            )}
            {poll.userAnswered && !editing && !isOwnerView && (
              <button
                onClick={() => setEditing(true)}
                className="text-tertiary hover:text-primary text-sm font-medium flex items-center gap-1 font-rubik"
                data-testid="edit-response-link"
              >
                <MdEdit className="text-base" />
                Edit
              </button>
            )}
          </div>
        </div>
        {pollTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {pollTags.map(tag => (
              <span
                key={tag.tagId}
                className="tag-pill text-white font-rubik"
                style={{ backgroundColor: tag.color }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Owner-specific info: timestamps */}
      {isOwnerView && (poll.status === 'active' || poll.status === 'closed' || poll.status === 'flagged') && (
        <div className="bg-blue-50 border border-blue-200 rounded-card p-3 mb-4">
          <div className="flex flex-wrap gap-4 text-sm font-rubik items-center">
            {/* Poll Type Badge */}
            {poll.durationDays && (
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold text-white ${getPollTypeLabel(poll.durationDays).color}`}>
                  <MdDateRange className="inline-block mr-1 text-sm align-text-bottom" />
                  {getPollTypeLabel(poll.durationDays).label}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <MdAccessTime className="text-blue-600" />
              <span className="text-gray-600">Activated:</span>
              <span className="font-medium text-gray-800">{formatDate(poll.approvedAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <MdAccessTime className="text-red-600" />
              <span className="text-gray-600">Expires:</span>
              <span className="font-medium text-gray-800">{formatDate(poll.expiresAt)}</span>
            </div>
            {poll.responseCount !== undefined && (
              <div className="flex items-center gap-2">
                <MdHowToVote className="text-green-600" />
                <span className="text-gray-600">Responses:</span>
                <span className="font-medium text-gray-800">{poll.responseCount}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rejection/Closure Reason */}
      {poll.status === 'rejected' && poll.rejectionReason && (
        <div className="bg-red-50 border border-red-200 rounded-card p-3 mb-4">
          <p className="text-sm text-red-800 font-rubik">
            <span className="font-semibold">Rejection Reason:</span> {poll.rejectionReason}
          </p>
          <p className="text-xs text-red-600 mt-1 font-rubik">
            Your {poll.pointsSpent} poll points have been refunded.
          </p>
        </div>
      )}

      {poll.status === 'closed' && poll.closureReason && (
        <div className="bg-orange-50 border border-orange-200 rounded-card p-3 mb-4">
          <p className="text-sm text-orange-800 font-rubik">
            <span className="font-semibold">Closed Early:</span> {poll.closureReason}
          </p>
        </div>
      )}

      {/* Pending Status Info */}
      {poll.status === 'pending' && isOwnerView && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-card p-3 mb-4">
          <p className="text-sm text-yellow-800 font-rubik flex items-center gap-2">
            <MdInfo className="text-lg" />
            Your poll is being processed. You spent {poll.pointsSpent} points for a {poll.durationDays}-day poll.
          </p>
        </div>
      )}

      {/* Voting Buttons - Only show on All Polls page (not owner view) */}
      {poll.status === 'active' && (!poll.userAnswered || editing) && !isOwnerView && (
        <div className="space-y-3">
          {poll.isOwner && !poll.userAnswered && (
            <div className="bg-blue-50 border border-blue-200 rounded-card p-2 mb-2">
              <p className="text-xs text-blue-700 font-rubik">As the poll owner, you can also vote on your own poll.</p>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => handleVote('yes')}
              disabled={voting}
              className="flex-1 btn-primary disabled:opacity-50"
              data-testid="custom-vote-yes-button"
            >
              Yes
            </button>
            <button
              onClick={() => handleVote('no')}
              disabled={voting}
              className="flex-1 btn-danger disabled:opacity-50"
              data-testid="custom-vote-no-button"
            >
              No
            </button>
          </div>
          {editing && (
            <button
              onClick={() => setEditing(false)}
              className="w-full text-gray-600 py-2 text-sm hover:text-gray-800 font-rubik"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* User's Answer Display - Show on All Polls page only */}
      {poll.status === 'active' && poll.userAnswered && !editing && !isOwnerView && (
        <div className="bg-green-50 border border-primary rounded-card p-4">
          <p className="text-primary font-medium font-rubik flex items-center gap-2">
            <MdHowToVote className="text-xl" />
            Your answer: <span className="font-bold capitalize">{poll.userAnswer}</span>
          </p>
        </div>
      )}

      {/* Loading Results */}
      {loadingResults && (
        <div className="mt-6 pt-6 border-t">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative w-48 h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary rounded-full animate-loading-bar"></div>
            </div>
            <p className="text-gray-600 font-rubik font-medium">Loading Results...</p>
          </div>
        </div>
      )}

      {/* Results Display with Demographic Filtering */}
      {showResults && results && (
        <div className="mt-6 pt-6 border-t" data-testid="custom-poll-results">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2">
              <MdHowToVote className="text-xl text-primary" />
              Results Analytics
              {poll.status === 'closed' && isOwnerView && (
                <span className="text-sm font-normal text-gray-500">(Only visible to you)</span>
              )}
            </h4>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* LEFT SIDE: Cumulative Results */}
            <div className="bg-gray-50 rounded-card p-6 border border-gray-200">
              <h5 className="text-base font-semibold text-gray-800 font-rubik mb-4">Cumulative Results</h5>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 font-rubik mb-2">
                  Total Votes: <span className="font-semibold text-gray-800">{results.totalResponses}</span>
                </p>
              </div>

              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={cumulativeBarChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontFamily: 'Rubik', fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontFamily: 'Rubik', fontSize: 14 }} width={50} />
                  <RechartsTooltip 
                    contentStyle={{ fontFamily: 'Rubik' }}
                    formatter={(value, name, props) => [
                      `${value} votes (${props.payload.percentage}%)`,
                      name
                    ]}
                  />
                  <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                    {cumulativeBarChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-primary"></div>
                    <span className="text-sm font-medium text-gray-700 font-rubik">Yes</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 font-rubik">{results.results.yes.percentage}% ({results.results.yes.count})</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-danger"></div>
                    <span className="text-sm font-medium text-gray-700 font-rubik">No</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 font-rubik">{results.results.no.percentage}% ({results.results.no.count})</span>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: Filtered Results with Demographic Filters */}
            <div className="bg-blue-50 rounded-card p-6 border border-blue-200">
              <h5 className="text-base font-semibold text-gray-800 font-rubik mb-4">Demographic Filtering</h5>

              {demographics && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      onClick={() => handleFilterChange('overall')}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'overall'
                          ? 'bg-primary text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                      data-testid="filter-clear"
                    >
                      Clear Filter
                    </button>
                    <button
                      onClick={() => handleFilterChange('country')}
                      disabled={!demographics.country.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'country'
                          ? 'bg-secondary text-white'
                          : demographics.country.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.country.available ? 'Update your profile with country info' : ''}
                      data-testid="filter-country"
                    >
                      Country
                    </button>
                    <button
                      onClick={() => handleFilterChange('state')}
                      disabled={!demographics.state.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'state'
                          ? 'bg-secondary text-white'
                          : demographics.state.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.state.available ? 'Update your profile with state info' : ''}
                      data-testid="filter-state"
                    >
                      State
                    </button>
                    <button
                      onClick={() => handleFilterChange('gender')}
                      disabled={!demographics.gender.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'gender'
                          ? 'bg-secondary text-white'
                          : demographics.gender.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.gender.available ? 'Update your profile with gender info' : ''}
                      data-testid="filter-gender"
                    >
                      Gender
                    </button>
                    <button
                      onClick={() => handleFilterChange('generation')}
                      disabled={!demographics.generation.available}
                      className={`px-3 py-1.5 rounded-card font-rubik font-medium text-xs transition-all ${
                        selectedFilter === 'generation'
                          ? 'bg-secondary text-white'
                          : demographics.generation.available
                          ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300'
                      }`}
                      title={!demographics.generation.available ? 'Update your profile with birthdate info' : ''}
                      data-testid="filter-generation"
                    >
                      Generation
                    </button>
                  </div>

                  {filterError && (
                    <div className="bg-red-50 border border-danger rounded-card p-3 mb-4">
                      <p className="text-danger font-rubik text-xs flex items-center gap-2">
                        <MdInfo className="text-sm" />
                        {filterError}
                      </p>
                      <Link to="/profile" className="text-xs text-tertiary hover:text-primary font-medium mt-1 inline-block">
                        → Update Profile
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Loading indicator for demographic breakdown */}
              {loadingBreakdown && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="relative w-32 h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary via-primary to-secondary rounded-full animate-loading-bar"></div>
                  </div>
                  <p className="text-sm text-gray-600 font-rubik">Loading breakdown...</p>
                </div>
              )}

              {/* Demographic Breakdown Chart */}
              {!loadingBreakdown && demographicBreakdown && selectedFilter !== 'overall' ? (
                <DemographicBreakdownChart 
                  data={demographicBreakdown.breakdown} 
                  demographic={selectedFilter}
                />
              ) : !loadingBreakdown && selectedFilter === 'overall' ? (
                <div className="text-center py-8">
                  <MdInfo className="text-4xl text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-rubik">
                    Select a demographic filter to view the breakdown
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomPollCard;
